import cn from 'classnames';
import { Icon } from '@pairy/pds-react';
import { MouseEvent } from 'react';
import { useAtom } from 'jotai';

import DesktopPdfDownloadButton from '../../@common/Layer/DefaultViewerLayer/PdfDownloadButton/Desktop';
import DesktopShareButton from '../../@common/Layer/DefaultViewerLayer/ShareButton/Desktop';
import DesktopLanguageButton from '../../@common/Layer/DefaultViewerLayer/LanguageButton/Desktop';
import { isDisableHelpersAtom } from '../../../store/index.atom';
import { V2DocumentType } from '../index.types';

import PageIndicator from './PageIndicator';
import ZoomControls from './ZoomControls';
import * as Styled from './index.styles';

interface Props {
  document: V2DocumentType;
  isBulk: boolean;
  domain?: string | null;
  /** 관리자 미리보기 모드(버전 전환 버튼 숨김) */
  isPreview?: boolean;
  ctaFormPage?: number;
  isRedirectCtaFormAppropriate?: boolean;
  isScrollModeEnabled: boolean;
  currentPage: number;
  goToPage: (page: number) => void;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  isNextPageDisabled: boolean;
  fitWidthZoom: number;
  isFullscreenActive: boolean;
  toggleFullscreen: () => void;
  translateKoToEn: (item: string) => string;
}

/**
 * V2 데스크톱 플로팅 컨트롤. V1과 동일하게 배경 바 없이
 * 좌상단(목록), 우상단(줌/언어/다운로드/공유/V1 전환), 하단 중앙(페이지 이동)으로
 * 배치하고, 화면 클릭으로 토글되는 isDisableHelpers 상태에 따라 반투명 처리한다.
 */
const Toolbar = ({
  document,
  isBulk,
  domain,
  ctaFormPage,
  isRedirectCtaFormAppropriate,
  currentPage,
  goToPage,
  goToPrevPage,
  goToNextPage,
  isNextPageDisabled,
  fitWidthZoom,
  isFullscreenActive,
  toggleFullscreen,
  translateKoToEn,
}: Props) => {
  const [isDisableHelpers, setIsDisableHelpers] = useAtom(isDisableHelpersAtom);
  // const { setVersion } = useViewerVersion();

  const dimmedClassName = cn({ disabled: isDisableHelpers });

  /* 흐려진 상태의 첫 클릭은 기능 실행 없이 컨트롤을 다시 켠다(V1 파리티) */
  const handleGuardClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDisableHelpers) {
      return;
    }
    e.stopPropagation();
    setIsDisableHelpers(false);
  };

  /* 컨트롤 영역 클릭이 뷰어의 헬퍼 토글로 전파되지 않게 한다 */
  const stopClickPropagation = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <>
      <Styled.TopRightWrapper
        className={dimmedClassName}
        onClickCapture={handleGuardClickCapture}
        onClick={stopClickPropagation}
      >
        <ZoomControls
          fitWidthZoom={fitWidthZoom}
          isFullscreenActive={isFullscreenActive}
          toggleFullscreen={toggleFullscreen}
          translateKoToEn={translateKoToEn}
        />
        {/* NOTE: 스크롤 보기 모드 전환 버튼은 임시 비활성화 상태
        {isScrollModeEnabled && (
          <ToolbarButton
            icon={viewMode === 'paged' ? 'ph:scroll' : 'ph:book-open'}
            label={
              viewMode === 'paged'
                ? translateKoToEn('view-mode-scroll')
                : translateKoToEn('view-mode-paged')
            }
            onClick={() =>
              setViewMode(viewMode === 'paged' ? 'scroll' : 'paged')
            }
          />
        )} */}
        <DesktopLanguageButton />
        <DesktopPdfDownloadButton
          translateKoToEn={translateKoToEn}
          fileName={document.name}
          isDownloadEnabled={document.isDownloadEnabled}
          ctaFormPage={ctaFormPage}
          isRedirectCtaFormAppropriate={isRedirectCtaFormAppropriate}
          isBulk={isBulk}
        />
        {document.isShareable && (
          <DesktopShareButton
            translateKoToEn={translateKoToEn}
            domain={domain}
            isBulk={isBulk}
          />
        )}
        {/*{!isPreview && (*/}
        {/*  <ToolbarButton*/}
        {/*    icon="ph:arrow-u-up-left"*/}
        {/*    label={translateKoToEn('viewer-switch-back')}*/}
        {/*    onClick={() => setVersion('v1')}*/}
        {/*  />*/}
        {/*)}*/}
      </Styled.TopRightWrapper>

      <Styled.BottomNavWrapper
        className={dimmedClassName}
        onClickCapture={handleGuardClickCapture}
        onClick={stopClickPropagation}
      >
        <Styled.NavArrowButton
          type="button"
          aria-label={translateKoToEn('prev-page')}
          title={translateKoToEn('prev-page')}
          disabled={currentPage <= 1}
          onClick={(e) => {
            e.stopPropagation();
            goToPrevPage();
          }}
        >
          <Icon icon="ph:caret-left" size={24} color="black" />
        </Styled.NavArrowButton>
        <PageIndicator
          currentPage={currentPage}
          totalPages={document.totalPages}
          goToPage={goToPage}
          translateKoToEn={translateKoToEn}
        />
        <Styled.NavArrowButton
          type="button"
          aria-label={translateKoToEn('next-page')}
          title={translateKoToEn('next-page')}
          disabled={currentPage >= document.totalPages || isNextPageDisabled}
          onClick={(e) => {
            e.stopPropagation();
            goToNextPage();
          }}
        >
          <Icon icon="ph:caret-right" size={24} color="black" />
        </Styled.NavArrowButton>
      </Styled.BottomNavWrapper>
    </>
  );
};

export default Toolbar;
